/**
 * makeScratch — named module-scope Three.js temporaries.
 *
 * Every viewport file that does heavy per-frame math hand-declares a
 * collection of `const _tmpV = new THREE.Vector3()` singletons to avoid
 * per-frame GC. `makeScratch` is a shorthand: pass a spec of name →
 * kind, receive a strongly-typed object holding one instance per entry.
 *
 * ## Semantics
 *
 * `makeScratch` is a plain factory — the returned object and its
 * entries are created eagerly at call time. Invoke it at module scope
 * so the pool outlives every render. This preserves the behavior of the
 * hand-rolled pattern exactly: the temporaries live for the lifetime of
 * the module, and any user can `.copy()`, `.set()`, or mutate them
 * freely.
 *
 * ## Usage
 *
 * ```ts
 * // at module scope
 * const tmp = makeScratch({
 *   pos: "vec3",
 *   rot: "quat",
 *   basis: "mat4",
 * });
 *
 * // inside a render loop
 * tmp.pos.copy(someVector);
 * tmp.rot.setFromEuler(euler);
 * tmp.basis.compose(tmp.pos, tmp.rot, ONE);
 * ```
 *
 * ## Concurrency caveat
 *
 * These temporaries are module singletons. Do not hand one to an async
 * boundary that might be resumed after another call site has mutated
 * it, and do not reuse the same name across unrelated callers in the
 * same file — split them into two pools if you need isolation.
 */

import * as THREE from "three/webgpu";

// ---------------------------------------------------------------------------
// Spec types — one entry per supported THREE constructor.
// ---------------------------------------------------------------------------

export type ScratchKind =
  | "vec3"
  | "vec2"
  | "quat"
  | "mat4"
  | "mat3"
  | "euler"
  | "color"
  | "ray"
  | "plane"
  | "box3"
  | "sphere"
  | "raycaster";

type ScratchType<K extends ScratchKind> = K extends "vec3"
  ? THREE.Vector3
  : K extends "vec2"
    ? THREE.Vector2
    : K extends "quat"
      ? THREE.Quaternion
      : K extends "mat4"
        ? THREE.Matrix4
        : K extends "mat3"
          ? THREE.Matrix3
          : K extends "euler"
            ? THREE.Euler
            : K extends "color"
              ? THREE.Color
              : K extends "ray"
                ? THREE.Ray
                : K extends "plane"
                  ? THREE.Plane
                  : K extends "box3"
                    ? THREE.Box3
                    : K extends "sphere"
                      ? THREE.Sphere
                      : K extends "raycaster"
                        ? THREE.Raycaster
                        : never;

export type ScratchPool<S extends Record<string, ScratchKind>> = {
  readonly [K in keyof S]: ScratchType<S[K]>;
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

function construct<K extends ScratchKind>(kind: K): ScratchType<K> {
  switch (kind) {
    case "vec3":
      return new THREE.Vector3() as ScratchType<K>;
    case "vec2":
      return new THREE.Vector2() as ScratchType<K>;
    case "quat":
      return new THREE.Quaternion() as ScratchType<K>;
    case "mat4":
      return new THREE.Matrix4() as ScratchType<K>;
    case "mat3":
      return new THREE.Matrix3() as ScratchType<K>;
    case "euler":
      return new THREE.Euler() as ScratchType<K>;
    case "color":
      return new THREE.Color() as ScratchType<K>;
    case "ray":
      return new THREE.Ray() as ScratchType<K>;
    case "plane":
      return new THREE.Plane() as ScratchType<K>;
    case "box3":
      return new THREE.Box3() as ScratchType<K>;
    case "sphere":
      return new THREE.Sphere() as ScratchType<K>;
    case "raycaster":
      return new THREE.Raycaster() as ScratchType<K>;
    default: {
      const _exhaustive: never = kind;
      throw new Error(`Unknown scratch kind: ${String(_exhaustive)}`);
    }
  }
}

/**
 * Allocate a named pool of THREE scratch temporaries. Call at module
 * scope so the pool lives for the module's lifetime.
 */
export function makeScratch<S extends Record<string, ScratchKind>>(
  spec: S,
): ScratchPool<S> {
  const pool: Record<string, unknown> = {};
  for (const key of Object.keys(spec) as Array<keyof S>) {
    pool[key as string] = construct(spec[key]);
  }
  return pool as ScratchPool<S>;
}
